import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PageIcon } from "@/components/PageIcon";
import { Plus, Pencil, Trash2, X, Loader2, ChevronDown, Info } from "lucide-react";

const fmtDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const selCls = "w-full appearance-none rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 pr-8 text-sm text-slate-900 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 focus:outline-none";

export default function CrewsPage() {
  const [crews, setCrews] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // "new" | crew

  const load = () => api.get("/crews").then((r) => { setCrews(r.data.crews || []); setCanManage(r.data.can_manage); }).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const del = async (c) => { if (!window.confirm(`Delete crew "${c.title}"?`)) return; try { await api.delete(`/crews/${c.id}`); toast.success("Crew deleted"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); } };

  return (
    <div className="px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8" data-testid="crews-page">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3"><PageIcon /><div>
          <h1 className="text-2xl font-bold text-slate-900">Crews</h1>
          <p className="text-sm text-slate-500 mt-1 flex items-start gap-1.5"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /> The actual groups of people you can assign to a shift.</p>
        </div></div>
        {canManage && <Button className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white shrink-0" onClick={() => setEditing("new")} data-testid="crew-add-btn"><Plus className="w-4 h-4" /> New Crew</Button>}
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        {loading ? <div className="p-8 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
          : crews.length === 0 ? <div className="p-10 text-center text-sm text-slate-400" data-testid="crews-empty">No Crews Found</div>
          : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr><th className="px-4 py-2.5">Title</th><th className="px-4 py-2.5">Members</th><th className="px-4 py-2.5">Assets</th><th className="px-4 py-2.5">Date</th><th className="px-4 py-2.5 w-16"></th></tr>
              </thead>
              <tbody>
                {crews.map((c) => (
                  <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`crew-row-${c.id}`}>
                    <td className="px-4 py-3 font-medium text-slate-800">{c.title}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(c.users || []).length === 0 ? <span className="text-slate-400">—</span> : c.users.map((u) => <span key={u.id} className="text-xs bg-indigo-50 text-indigo-700 rounded-full px-2 py-0.5">{u.name}</span>)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(c.assets || []).length === 0 ? <span className="text-slate-400">—</span> : c.assets.map((a) => <span key={a.legacy_id} className="text-xs bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{a.title}</span>)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{fmtDate(c.date)}</td>
                    <td className="px-4 py-3">
                      {canManage && (
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => setEditing(c)} className="text-slate-400 hover:text-indigo-600" title="Edit"><Pencil className="w-4 h-4" /></button>
                          <button onClick={() => del(c)} className="text-slate-400 hover:text-rose-600" title="Delete" data-testid={`crew-delete-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      {editing && <CrewForm crew={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function MultiPick({ label, options, value, onChange, getKey, getLabel, testid }) {
  const has = (o) => value.some((v) => getKey(v) === getKey(o));
  const add = (key) => { const o = options.find((x) => String(getKey(x)) === String(key)); if (o && !has(o)) onChange([...value, o]); };
  const remove = (o) => onChange(value.filter((v) => getKey(v) !== getKey(o)));
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</label>
      <div className="mt-1 rounded-lg border border-slate-200 bg-slate-50 p-2">
        {value.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {value.map((o) => (
              <span key={getKey(o)} className="inline-flex items-center gap-1 text-xs bg-indigo-100 text-indigo-700 rounded-full pl-2 pr-1 py-0.5">
                {getLabel(o)}<button onClick={() => remove(o)} className="hover:text-rose-600"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        )}
        <div className="relative">
          <select value="" onChange={(e) => { add(e.target.value); e.target.value = ""; }} className={selCls} data-testid={testid}>
            <option value="">Please select…</option>
            {options.filter((o) => !has(o)).map((o) => <option key={getKey(o)} value={getKey(o)}>{getLabel(o)}</option>)}
          </select>
          <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        </div>
      </div>
    </div>
  );
}

function CrewForm({ crew, onClose, onSaved }) {
  const [title, setTitle] = useState(crew?.title || "");
  const [users, setUsers] = useState(crew?.users || []);
  const [assets, setAssets] = useState(crew?.assets || []);
  const [date, setDate] = useState(crew?.date || "");
  const [opts, setOpts] = useState({ users: [], assets: [] });
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/crews/options").then((r) => setOpts(r.data)).catch(() => {}); }, []);

  const save = async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    const payload = { title: title.trim(), users, assets, date: date || null };
    try {
      if (crew?.id) { await api.put(`/crews/${crew.id}`, payload); toast.success("Crew saved"); }
      else { await api.post("/crews", payload); toast.success("Crew created"); }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save crew"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex justify-end" onClick={onClose} data-testid="crew-form">
      <div className="bg-white h-full w-full sm:w-1/2 sm:max-w-none shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-300" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="text-xl font-bold text-slate-900">Crew Form</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-3 text-sm text-slate-500 border-b border-slate-50">Basic information about the crew</div>
        <div className="p-6 space-y-4">
          <div><label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Title</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" data-testid="crew-title" placeholder="e.g. Crew A" /></div>
          <MultiPick label="Users" options={opts.users} value={users} onChange={setUsers} getKey={(o) => o.id} getLabel={(o) => o.name} testid="crew-users" />
          <MultiPick label="Assets" options={opts.assets} value={assets} onChange={setAssets} getKey={(o) => o.legacy_id} getLabel={(o) => o.title} testid="crew-assets" />
          <div><label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Date</label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1" data-testid="crew-date" /></div>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={busy} onClick={save} data-testid="crew-save">{busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Save</Button>
        </div>
      </div>
    </div>
  );
}
